import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Trophy, Flame, Target, Calendar, Users, Eye, Brain, Crown, 
  Award, Medal, Star, Zap, Shield, TrendingUp, Footprints, Share2, CalendarCheck
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface Achievement {
  id: string;
  badge_type: string;
  badge_name: string;
  description: string;
  icon_name: string;
  tier: string;
  points_value: number;
  requirements: any;
}

interface UserBadge {
  id: string;
  badge_name: string;
  badge_type: string;
  description: string;
  earned_at: string;
  icon_url: string;
  metadata: any;
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  challenge_type: string;
  requirements: any;
  reward_points: number;
  starts_at: string;
  ends_at: string;
  progress?: number;
  completed?: boolean;
}

interface UserStreak {
  streak_type: string;
  current_count: number;
  best_count: number;
}

const iconMap: Record<string, any> = {
  Flame, Target, Calendar, Users, Eye, Brain, Crown, Award, Medal, 
  Star, Zap, Shield, TrendingUp, Footprints, Share2, CalendarCheck, Trophy
};

const tierColors = {
  bronze: 'from-amber-700 to-amber-600',
  silver: 'from-gray-400 to-gray-300',
  gold: 'from-yellow-500 to-yellow-400',
  platinum: 'from-purple-500 to-purple-400',
};

export function GamifiedAchievements() {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [streaks, setStreaks] = useState<UserStreak[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAchievementsData();
  }, [user]);

  const fetchAchievementsData = async () => {
    try {
      // Fetch all achievement definitions
      const { data: achievementsData } = await supabase
        .from('achievement_definitions')
        .select('*')
        .eq('is_active', true)
        .order('tier', { ascending: true });

      setAchievements(achievementsData || []);

      // Fetch active challenges
      const { data: challengesData } = await supabase
        .from('seasonal_challenges')
        .select('*')
        .eq('is_active', true)
        .gte('ends_at', new Date().toISOString())
        .order('ends_at', { ascending: true });

      if (user) {
        // Fetch user's earned badges
        const { data: badgesData } = await supabase
          .from('user_badges')
          .select('*')
          .eq('user_id', user.id);

        setUserBadges(badgesData || []);

        // Fetch user's streaks
        const { data: streaksData } = await supabase
          .from('user_streaks')
          .select('*')
          .eq('user_id', user.id);

        setStreaks(streaksData || []);

        // Fetch challenge progress
        const { data: progressData } = await supabase
          .from('challenge_progress')
          .select('*')
          .eq('user_id', user.id);

        // Merge progress with challenges
        const challengesWithProgress = (challengesData || []).map(challenge => {
          const progress = progressData?.find(p => p.challenge_id === challenge.id);
          return {
            ...challenge,
            progress: progress?.progress || 0,
            completed: progress?.completed || false,
          };
        });

        setChallenges(challengesWithProgress);
      } else {
        setChallenges(challengesData || []);
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const earnedBadgeNames = userBadges.map(b => b.badge_name);

  const getTimeRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  if (loading) {
    return (
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3" />
            <div className="grid grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <div key={i} className="h-24 bg-muted rounded" />)}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Streaks Overview */}
      <Card className="bg-gradient-to-br from-orange-500/20 via-card to-card border-orange-500/30">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold flex items-center gap-2 mb-4">
            <Flame className="h-5 w-5 text-orange-500" />
            Your Streaks
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['daily_login', 'winning', 'betting'].map((type) => {
              const streak = streaks.find(s => s.streak_type === type) || { current_count: 0, best_count: 0 };
              const labels: Record<string, string> = {
                daily_login: 'Login Streak',
                winning: 'Win Streak',
                betting: 'Betting Streak',
              };
              return (
                <div key={type} className="text-center p-4 rounded-lg bg-muted/30">
                  <Flame className={`h-8 w-8 mx-auto mb-2 ${streak.current_count > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
                  <div className="text-2xl font-bold">{streak.current_count}</div>
                  <div className="text-sm text-muted-foreground">{labels[type]}</div>
                  <div className="text-xs text-muted-foreground mt-1">Best: {streak.best_count}</div>
                </div>
              );
            })}
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <Trophy className="h-8 w-8 mx-auto mb-2 text-primary" />
              <div className="text-2xl font-bold">{userBadges.length}</div>
              <div className="text-sm text-muted-foreground">Badges Earned</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="challenges" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="challenges" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Challenges
          </TabsTrigger>
          <TabsTrigger value="achievements" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Achievements
          </TabsTrigger>
          <TabsTrigger value="earned" className="flex items-center gap-2">
            <Medal className="h-4 w-4" />
            Earned ({userBadges.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="challenges" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {challenges.map((challenge) => (
              <Card key={challenge.id} className={`bg-card/50 border-border/50 ${challenge.completed ? 'border-green-500/50' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">{challenge.title}</h4>
                      <p className="text-sm text-muted-foreground">{challenge.description}</p>
                    </div>
                    <Badge variant={challenge.completed ? 'default' : 'outline'} className={challenge.completed ? 'bg-green-500' : ''}>
                      {challenge.completed ? 'Completed' : getTimeRemaining(challenge.ends_at)}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{challenge.progress || 0}/{challenge.requirements?.count || 0}</span>
                    </div>
                    <Progress 
                      value={((challenge.progress || 0) / (challenge.requirements?.count || 1)) * 100} 
                      className="h-2"
                    />
                  </div>

                  <div className="mt-3 flex items-center gap-2">
                    <Zap className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">+{challenge.reward_points} points</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="achievements" className="space-y-6">
          {['streak', 'activity', 'loyalty', 'social', 'performance'].map((category) => {
            const categoryAchievements = achievements.filter(a => a.badge_type === category);
            if (categoryAchievements.length === 0) return null;
            
            const labels: Record<string, string> = {
              streak: 'Streak Achievements',
              activity: 'Activity Achievements',
              loyalty: 'Loyalty Achievements',
              social: 'Social Achievements',
              performance: 'Performance Achievements',
            };

            return (
              <div key={category}>
                <h4 className="text-lg font-semibold mb-3">{labels[category]}</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {categoryAchievements.map((achievement) => {
                    const isEarned = earnedBadgeNames.includes(achievement.badge_name);
                    const IconComponent = iconMap[achievement.icon_name] || Trophy;
                    const tierColor = tierColors[achievement.tier as keyof typeof tierColors];
                    
                    return (
                      <Card 
                        key={achievement.id} 
                        className={`relative overflow-hidden ${isEarned ? '' : 'opacity-50 grayscale'}`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${tierColor} opacity-10`} />
                        <CardContent className="p-4 text-center relative">
                          <div className={`w-12 h-12 mx-auto mb-2 rounded-full bg-gradient-to-br ${tierColor} flex items-center justify-center`}>
                            <IconComponent className="h-6 w-6 text-white" />
                          </div>
                          <h5 className="font-semibold text-sm">{achievement.badge_name}</h5>
                          <p className="text-xs text-muted-foreground mt-1">{achievement.description}</p>
                          <Badge variant="outline" className="mt-2 text-xs capitalize">
                            {achievement.tier}
                          </Badge>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="earned" className="space-y-4">
          {userBadges.length === 0 ? (
            <Card className="bg-card/50 border-border/50">
              <CardContent className="p-8 text-center">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">No badges earned yet</h3>
                <p className="text-muted-foreground">
                  Complete challenges and achievements to earn badges!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {userBadges.map((badge) => (
                <Card key={badge.id} className="bg-card/50 border-primary/30">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-primary/20 flex items-center justify-center">
                      <Trophy className="h-6 w-6 text-primary" />
                    </div>
                    <h5 className="font-semibold text-sm">{badge.badge_name}</h5>
                    <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                    <p className="text-xs text-primary mt-2">
                      Earned {new Date(badge.earned_at).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
